import React, { useState, useRef, useEffect } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import '../../../styles/custom-quill-toolbar.css';
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  Button,
  DropdownItem,
  image,
} from '@nextui-org/react';
import { message, Spin } from 'antd';
import { PrivateAxiosInstance } from '../../../services/createAxiosInstance';
import { URLs } from '../../../constants/apiEndpoints';
import FileuploadInput from '../../../components/FileuploadInput/FileuploadInput';
import { useNavigate } from 'react-router-dom';
import { articleValidate } from '../../../utils/Validation/ArticleConsstraint';

function AddMarketAnalysis() {
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState('market');
  const [formData, setFormData] = useState({
    articleName: '',
    description: '',
    status: false,
    image: null,
  });

  const editorRef = useRef(null);

  const navigate = useNavigate();

  useEffect(() => {
    const quill = new Quill(editorRef.current, {
      theme: 'snow',
      modules: {
        toolbar: [
          [{ header: '1' }, { header: '2' }, { header: '3' }],
          [{ indent: '-1' }, { indent: '+1' }],
          [{ list: 'ordered' }, { list: 'bullet' }, { list: 'bullet' }],
          [{ align: [] }],
          ['bold', 'italic', 'underline', 'strike'],
          ['link'],
        ],
      },
    });

    quill.on('text-change', () => {
      const descriptionContent = quill.root.innerHTML;
      setFormData((prevData) => ({
        ...prevData,
        description: descriptionContent,
      }));
    });
  }, []);

  const handleSelect = (key) => {
    setSelectedKeys(key);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const errors = await articleValidate(formData);

    if (errors && Object.keys(errors).length > 0) {
      setErrors(errors);
      setLoading(false);
      return;
    }

    if (!formData.image) {
      message.error('Image is required');
      setLoading(false);
      return;
    }

    const formDataToSend = new FormData();

    if (selectedKeys === 'community') {
      formDataToSend.append('status', true);
    } else {
      formDataToSend.append('status', false);
    }

    formDataToSend.append('articleName', formData.articleName);
    formDataToSend.append('description', formData.description);
    formDataToSend.append('files', formData.image);

    try {
      const res = await PrivateAxiosInstance.post(
        URLs.CREATE_ARTICLE,
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      message.success('Article Added Successfully');
      setFormData({ articleName: '', description: '', image: null });
      navigate('/admin/market-analysis/all');
    } catch (error) {
      console.error('Article submit error:', error);
      message.error(error?.response?.data?.error || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const { files } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      image: files[0],
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  return (
    <section className="bg-background-primary-color">
      <Spin tip="Uploading..." spinning={loading} fullscreen />
      <div className="flex flex-col items-center justify-center mt-20 mb-20 mx-auto lg:py-0">
        <div className="w-full rounded-lg shadow md:min-w-[800px] lg:min-w-[1000px] md:mt-0 sm:max-w-md xl:p-0 bg-gray-800 ">
          <div className="p-6 space-y-4 md:space-y-6 sm:p-8">
            <h1 className="text-xl font-poppins font-bold leading-tight tracking-tight text-gray-200 md:text-2xl">
              Create a new Article
            </h1>
            <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
              <label
                htmlFor="articleName"
                className="block -mb-2 text-sm font-poppins font-medium text-gray-200"
              >
                Select type
              </label>
              <Dropdown>
                <DropdownTrigger>
                  <Button
                    // isDisabled
                    variant="solid"
                    className="text-md font-poppins bg-btn-color font-normal text-black"
                  >
                    {selectedKeys === 'market'
                      ? 'Market Analysis'
                      : 'Community Article'}
                  </Button>
                </DropdownTrigger>
                <DropdownMenu
                  color="success"
                  aria-label="Static Actions"
                  onAction={handleSelect}
                >
                  <DropdownItem key="market">Market Analysis</DropdownItem>
                  <DropdownItem key="community">Community Article</DropdownItem>
                </DropdownMenu>
              </Dropdown>

              <div>
                <label
                  htmlFor="articleName"
                  className="block mb-2 text-sm font-poppins font-medium text-gray-200"
                >
                  Title
                </label>
                <input
                  type="text"
                  id="articleName"
                  name="articleName"
                  value={formData.articleName}
                  onChange={handleChange}
                  placeholder="Add Title"
                  className="block w-full p-2 text-gray-900 border border-gray-300 rounded-lg bg-gray-50 text-sm focus:ring-blue-500 focus:border-blue-500  "
                />
                <span className="text-red-500 text-sm mt-2 block">
                  {errors.articleName || <>&nbsp;</>}
                </span>
              </div>

              <label
                htmlFor="description"
                className="block mb-2 font-poppins text-sm font-medium text-gray-200"
              >
                Description
              </label>

              <div
                className="bg-white text-black text-md h-96 rounded-lg"
                ref={editorRef}
              />

              <span className="text-red-500 text-sm mt-2 block">
                {errors.description || <>&nbsp;</>}
              </span>

              <div>
                <label
                  htmlFor="image"
                  className="font-poppins block mb-1 text-sm font-medium text-gray-200"
                >
                  Upload cover image
                </label>
                <FileuploadInput
                  name="image"
                  accept="image/jpeg,image/png"
                  onChange={handleFileChange}
                />
              </div>

              <button
                type="submit"
                className="flex justify-end font-poppins w-[100px] text-black bg-btn-color transition-all duration-300 hover:bg-btn-hover-color focus:ring-4 focus:outline-none focus:ring-gray-600 font-medium rounded-lg text-sm px-5 py-2.5 text-center "
              >
                Submit
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default AddMarketAnalysis;
